import React, { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { ExternalSource } from '../../../constants/Source';

import {
  ComputerIcon,
  DeviantArtIcon,
  DribbbleIcon,
  DropboxIcon,
  FacebookIcon,
  FlickrIcon,
  GoogleDriveIcon,
  GooglePhotosIcon,
  InstagramIcon,
  LinkIcon,
  VimeoIcon,
  WixIcon,
} from '../../../components/common/SourceIcon/Icons';
import { IMediaManagerSDKRequestOptions } from '../../../utils/media-manager-sdk';

export enum FilesViewerView {
  List = 'list',
  Gallery = 'gallery',
}

type TSourcesConfigs = {
  [key in ExternalSource]: {
    mediaManagerSDKName?: string;
    IconComponent: FC;
    emptyRootFolderTitle?: ReactNode;
    emptyRootFolderMessage?: ReactNode;
    emptyFolderTitle?: ReactNode;
    emptyFolderMessage?: ReactNode;
    notConnectedMessage?: ReactNode;
    loaderOptions?: IMediaManagerSDKRequestOptions;
    view?: {
      root?: FilesViewerView;
      nested?: FilesViewerView;
    };
  };
};

export const ExternalSourcesConfigs: TSourcesConfigs = {
  [ExternalSource.GoogleDrive]: {
    mediaManagerSDKName: 'Drive',
    IconComponent: GoogleDriveIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.google-drive" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.google-drive" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.google-drive" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.List,
    },
  },
  [ExternalSource.Dropbox]: {
    mediaManagerSDKName: 'Dropbox',
    IconComponent: DropboxIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.dropbox" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.dropbox" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.dropbox" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.List,
    },
  },
  [ExternalSource.Vimeo]: {
    mediaManagerSDKName: 'Vimeo',
    IconComponent: VimeoIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.vimeo" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.vimeo" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.vimeo" />,
    view: {
      root: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.Instagram]: {
    mediaManagerSDKName: 'Instagram',
    IconComponent: InstagramIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.instagram" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.instagram" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.instagram" />,
    view: {
      root: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.Facebook]: {
    mediaManagerSDKName: 'Facebook',
    IconComponent: FacebookIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.facebook" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.facebook" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.facebook" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.Flickr]: {
    mediaManagerSDKName: 'Flickr',
    IconComponent: FlickrIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.flickr" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.flickr" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.flickr" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.DeviantArt]: {
    mediaManagerSDKName: 'DeviantArt',
    IconComponent: DeviantArtIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.deviant-art" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.deviant-art" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.deviant-art" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.Gallery,
    },
    loaderOptions: {
      paging: {
        size: 20, // Maximum page size on DeviantArt at the moment of writing this code is 24 items per page
      },
    },
  },
  [ExternalSource.Dribbble]: {
    mediaManagerSDKName: 'Dribbble',
    IconComponent: DribbbleIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.dribble" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.dribble" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.dribble" />,
    view: {
      root: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.GooglePhotos]: {
    mediaManagerSDKName: 'GooglePhotos',
    IconComponent: GooglePhotosIcon,
    notConnectedMessage: <FormattedMessage id="files-importer.not-connected.google-photos" />,
    emptyRootFolderTitle: <FormattedMessage id="files-importer.empty-root-folder.title" />,
    emptyRootFolderMessage: <FormattedMessage id="files-importer.empty-root-folder.message.google-photos" />,
    emptyFolderTitle: <FormattedMessage id="files-importer.empty-folder.title" />,
    emptyFolderMessage: <FormattedMessage id="files-importer.empty-folder.message.google-photos" />,
    view: {
      root: FilesViewerView.List,
      nested: FilesViewerView.Gallery,
    },
  },
  [ExternalSource.Link]: {
    IconComponent: LinkIcon,
  },
  [ExternalSource.Computer]: {
    IconComponent: ComputerIcon,
  },
  [ExternalSource.WixSite]: {
    IconComponent: WixIcon,
    emptyRootFolderTitle: <FormattedMessage id="empty.state.board.no-files-title" />,
    emptyRootFolderMessage: <FormattedMessage id="empty.state.board.no-files-subtitle" />,
    emptyFolderTitle: <FormattedMessage id="empty.state.folder.no-files-title" />,
    emptyFolderMessage: <FormattedMessage id="empty.state.folder.no-files-subtitle" />,
  },
  [ExternalSource.WixVideo]: {
    IconComponent: WixIcon,
    emptyRootFolderTitle: <FormattedMessage id="empty.state.board.no-files-title" />,
    emptyRootFolderMessage: <FormattedMessage id="empty.state.board.no-files-subtitle" />,
    emptyFolderTitle: <FormattedMessage id="empty.state.folder.no-files-title" />,
    emptyFolderMessage: <FormattedMessage id="empty.state.folder.no-files-subtitle" />,
  },
};
